import { forwardRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IMaskInput } from 'react-imask';
import { getDocumentMasks } from '@/utils/documentMask';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input, inputClassName } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import ClubPhoneInput from './ClubPhoneInput';

const cardUpdateSchema = z.object({
    holderName: z.string().min(1, 'Informe o nome no cartão'),
    cardNumber: z.string().min(13, 'Informe o número do cartão'),
    expiryMonth: z.string().min(1, 'Informe o mês de validade'),
    expiryYear: z.string().min(2, 'Informe o ano de validade'),
    ccv: z.string().min(3, 'Informe o CVV'),
    cpfCnpj: z.string().min(11, 'Informe o CPF ou CNPJ'),
    email: z.string().min(1, 'Informe o e-mail').email('E-mail inválido'),
    phone: z.string().min(10, 'Informe o telefone'),
    postalCode: z.string().min(8, 'Informe o CEP'),
    addressNumber: z.string().min(1, 'Informe o número do endereço'),
});

const CardNumberInput = forwardRef(function CardNumberInput({ onChange, onBlur, name, value, ...props }, ref) {
    return (
        <IMaskInput
            {...props}
            name={name}
            value={String(value ?? '')}
            inputRef={ref}
            onBlur={onBlur}
            onAccept={maskedValue => onChange?.(maskedValue)}
            mask="0000 0000 0000 0000"
            unmask
            className={inputClassName}
        />
    );
});

const ExpiryInput = forwardRef(function ExpiryInput({ onChange, onBlur, name, value, mask, ...props }, ref) {
    return (
        <IMaskInput
            {...props}
            name={name}
            value={String(value ?? '')}
            inputRef={ref}
            onBlur={onBlur}
            onAccept={maskedValue => onChange?.(maskedValue)}
            mask={mask}
            unmask
            className={inputClassName}
        />
    );
});

const CpfCnpjInput = forwardRef(function CpfCnpjInput({ onChange, onBlur, name, value, ...props }, ref) {
    return (
        <IMaskInput
            {...props}
            name={name}
            value={String(value ?? '')}
            inputRef={ref}
            onBlur={onBlur}
            onAccept={maskedValue => onChange?.(maskedValue)}
            mask={getDocumentMasks('auto')}
            prepareChar={str => (/[a-z]/.test(str) ? str.toUpperCase() : str)}
            unmask
            className={inputClassName}
        />
    );
});

const CepInput = forwardRef(function CepInput({ onChange, onBlur, name, value, ...props }, ref) {
    return (
        <IMaskInput
            {...props}
            name={name}
            value={String(value ?? '')}
            inputRef={ref}
            onBlur={onBlur}
            onAccept={maskedValue => onChange?.(maskedValue)}
            mask="00000-000"
            unmask
            className={inputClassName}
        />
    );
});

function FormSection({ title, description, children }) {
    return (
        <div className="space-y-2">
            <div>
                <h3 className="text-sm font-medium">{title}</h3>
                {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
            </div>
            <FieldGroup className="gap-2">{children}</FieldGroup>
        </div>
    );
}

export default function CardUpdateForm({ formId = 'card-update-form', onSuccess }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();

    const form = useForm({
        resolver: zodResolver(cardUpdateSchema),
        defaultValues: {
            holderName: '',
            cardNumber: '',
            expiryMonth: '',
            expiryYear: '',
            ccv: '',
            cpfCnpj: '',
            email: '',
            phone: '',
            postalCode: '',
            addressNumber: '',
        },
    });

    const onSubmit = async values => {
        try {
            await confirm({
                title: 'Atualizar dados do cartão',
                description:
                    'O cartão será atualizado na assinatura para as próximas cobranças, sem gerar cobrança imediata.',
            });

            await server.put('club/payment/asaas/subscriptions/card', values);
            enqueueSnackbar('Dados do cartão atualizados com sucesso.');
            onSuccess?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;

    return (
        <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Alert>
                <AlertDescription>
                    A alteração vale para as próximas cobranças da assinatura. Nenhuma cobrança extra é feita ao salvar.
                </AlertDescription>
            </Alert>

            <FormSection title="Dados do cartão" description="Informações impressas no cartão de crédito.">
                <Field data-invalid={Boolean(form.formState.errors.holderName)}>
                    <FieldLabel htmlFor="card-holder-name">Nome no cartão</FieldLabel>
                    <Input id="card-holder-name" {...form.register('holderName')} aria-invalid={Boolean(form.formState.errors.holderName)} />
                    <FieldError>{form.formState.errors.holderName?.message}</FieldError>
                </Field>

                <Field data-invalid={Boolean(form.formState.errors.cardNumber)}>
                    <FieldLabel htmlFor="card-number">Número do cartão</FieldLabel>
                    <Controller
                        name="cardNumber"
                        control={form.control}
                        render={({ field }) => <CardNumberInput id="card-number" {...field} />}
                    />
                    <FieldError>{form.formState.errors.cardNumber?.message}</FieldError>
                </Field>

                <div className="grid gap-2 sm:grid-cols-3">
                    <Field data-invalid={Boolean(form.formState.errors.expiryMonth)}>
                        <FieldLabel htmlFor="card-expiry-month">Mês</FieldLabel>
                        <Controller
                            name="expiryMonth"
                            control={form.control}
                            render={({ field }) => (
                                <ExpiryInput id="card-expiry-month" mask="00" placeholder="MM" {...field} />
                            )}
                        />
                        <FieldError>{form.formState.errors.expiryMonth?.message}</FieldError>
                    </Field>

                    <Field data-invalid={Boolean(form.formState.errors.expiryYear)}>
                        <FieldLabel htmlFor="card-expiry-year">Ano</FieldLabel>
                        <Controller
                            name="expiryYear"
                            control={form.control}
                            render={({ field }) => (
                                <ExpiryInput id="card-expiry-year" mask="0000" placeholder="AAAA" {...field} />
                            )}
                        />
                        <FieldError>{form.formState.errors.expiryYear?.message}</FieldError>
                    </Field>

                    <Field data-invalid={Boolean(form.formState.errors.ccv)}>
                        <FieldLabel htmlFor="card-ccv">CVV</FieldLabel>
                        <Controller
                            name="ccv"
                            control={form.control}
                            render={({ field }) => <ExpiryInput id="card-ccv" mask="0000" placeholder="CVV" {...field} />}
                        />
                        <FieldError>{form.formState.errors.ccv?.message}</FieldError>
                    </Field>
                </div>
            </FormSection>

            <Separator />

            <FormSection title="Titular" description="Informações do titular do cartão.">
                <Field data-invalid={Boolean(form.formState.errors.cpfCnpj)}>
                    <FieldLabel htmlFor="card-cpf-cnpj">CPF/CNPJ do titular</FieldLabel>
                    <Controller
                        name="cpfCnpj"
                        control={form.control}
                        render={({ field }) => <CpfCnpjInput id="card-cpf-cnpj" {...field} />}
                    />
                    <FieldError>{form.formState.errors.cpfCnpj?.message}</FieldError>
                </Field>
             
                <Field data-invalid={Boolean(form.formState.errors.email)}>
                    <FieldLabel htmlFor="card-email">E-mail</FieldLabel>
                    <Input
                        id="card-email"
                        type="email"
                        autoComplete="email"
                        {...form.register('email')}
                        aria-invalid={Boolean(form.formState.errors.email)}
                    />
                    <FieldError>{form.formState.errors.email?.message}</FieldError>
                </Field>

                <Field data-invalid={Boolean(form.formState.errors.phone)}>
                    <FieldLabel htmlFor="card-phone">Telefone</FieldLabel>
                    <Controller
                        name="phone"
                        control={form.control}
                        render={({ field }) => (
                            <ClubPhoneInput
                                id="card-phone"
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                    <FieldError>{form.formState.errors.phone?.message}</FieldError>
                </Field>
             
                <div className="grid gap-2 sm:grid-cols-2">
                    <Field data-invalid={Boolean(form.formState.errors.postalCode)}>
                        <FieldLabel htmlFor="card-postal-code">CEP</FieldLabel>
                        <Controller
                            name="postalCode"
                            control={form.control}
                            render={({ field }) => <CepInput id="card-postal-code" {...field} />}
                        />
                        <FieldError>{form.formState.errors.postalCode?.message}</FieldError>
                    </Field>

                    <Field data-invalid={Boolean(form.formState.errors.addressNumber)}>
                        <FieldLabel htmlFor="card-address-number">Número</FieldLabel>
                        <Input id="card-address-number" {...form.register('addressNumber')} aria-invalid={Boolean(form.formState.errors.addressNumber)} />
                        <FieldError>{form.formState.errors.addressNumber?.message}</FieldError>
                    </Field>
                </div>
            </FormSection>

            {isSubmitting ? (
                <div className="flex justify-center py-2">
                    <Spinner className="size-5" />
                </div>
            ) : null}
        </form>
    );
}
